import React, {
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";
import { io } from "socket.io-client";
import { sendRequest } from "../api";
import { useUser } from './UserProvider';

const DownloadWebsocketContext = createContext({});
export const useDownloadWebsocket = () => useContext(DownloadWebsocketContext);

const ErrorMessage = ({ children }) => (
  <div className="fixed top-4 right-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded ">
    {children}
  </div>
);

const DownloadWebsocketProvider = ({ children }) => {
  const { user } = useUser();
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(5);
  const [videoUrl, setVideoUrl] = useState(null);
  const [socket, setSocket] = useState(null);
  const [polling, setPolling] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [completedVideos, setCompletedVideos] = useState(0);
  const [videosCount, setVideosCount] = useState(0);
  const [failedCount, setFailedCount] = useState(0);
  const [downloadId, setDownloadId] = useState(null);
  const [failures, setFailures] = useState([]);
  const [downloadProgress, setDownloadProgress] = useState(null);

  // Reset all states when downloadId changes
  useEffect(() => {
    if (!downloadId) {
      setLoading(true);
      setProgress(5);
      setVideoUrl(null);
      setPolling(false);
      setErrorMessage(null);
      setCompletedVideos(0);
      setVideosCount(0);
      setFailedCount(0);
      setFailures([]);
      
      // Cleanup socket connection
      if (socket) {
        socket.off("message", handleEvent);
        socket.disconnect();
        setSocket(null);
      }
    } else {
      // Reset failure states when starting a new download
      setFailedCount(0);
      setFailures([]);
    }
  }, [downloadId]);

  // Socket event handler
  const handleEvent = (data) => {
    if (downloadId && data && data.startsWith(downloadId)) {
      if (data.includes('-failures-')) {
        const [_, __, count] = data.split('-');
        const failedCount = parseInt(count);
        setFailedCount(failedCount);
      } else {
        const [_, progress, completed, total] = data.split('-');
        setProgress(parseInt(progress));
        if (completed && total > 0) {
          setCompletedVideos(parseInt(completed));
          setVideosCount(parseInt(total));
        }
        if (progress === "100") {
          setPolling(true);
        }
      }
    }
  };

  // Fetch failures when failedCount changes
  useEffect(() => {
    if (failedCount > 0) {
      fetchFailures();
    }
  }, [failedCount]);

  const fetchFailures = async () => {
    try {
      const { failed_video_urls } = await sendRequest(`/downloads/${downloadId}/failures`);
      if (failed_video_urls) {
        setFailures(failed_video_urls.map(f => {
          try {
            return JSON.parse(f);
          } catch (e) {
            console.error('Error parsing failure:', e);
            return [f, 'Unknown error'];
          }
        }));
      }
    } catch (error) {
      console.error('Error fetching failures:', error);
      setErrorMessage(error.message);
    }
  };

  // Socket connection
  useEffect(() => {
    if (!socket && !polling && downloadId) {
      const newSocket = io(process.env.WEBSOCKET_URL);
      setSocket(newSocket);
      newSocket.on("message", handleEvent);
    }

    return () => {
      if (socket) {
        socket.off("message", handleEvent);
        socket.disconnect();
        setSocket(null);
      }
    };
  }, [downloadId]);

  // Polling logic
  useEffect(() => {
    if (polling) {
      const interval = setInterval(async () => {
        try {
          const { zip_url } = await sendRequest(`/downloads/${downloadId}`);
          if (zip_url) {
            setVideoUrl(zip_url);
            clearInterval(interval);
            setLoading(false);
            setPolling(false);
            setProgress(100);
          }
        } catch (error) {
          if (error.status === 401) {
            window.location.reload()
          }
          setErrorMessage(error.message);
          setLoading(false)
          setPolling(false);
        } 
      }, 2500);

      return () => clearInterval(interval);
    }
  }, [polling]);

  const resetProgress = () => {
    setLoading(true);
    setProgress(5);
    setVideoUrl(null);
    setPolling(false);
    setErrorMessage(null);
    setCompletedVideos(0);
    setVideosCount(0);
    setFailedCount(0);
    setFailures([]);
    setDownloadId(null);
  };

  const value = {
    socket,
    progress,
    downloadProgress,
    completedVideos,
    videosCount,
    failedCount,
    failures,
    loading,
    videoUrl,
    polling,
    errorMessage,
    setDownloadId,
    resetProgress,
  };

  return (
    <DownloadWebsocketContext.Provider value={value}>
      <>
        {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
        {children}
      </>
    </DownloadWebsocketContext.Provider>
  );
};

export default DownloadWebsocketProvider; 